import {
  ArrowBack,
  ArrowForward,
  CancelOutlined,
  CommentOutlined,
  EditOutlined,
  ErrorOutline,
  ShareOutlined,
} from "@mui/icons-material";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import { Box, Button, Chip, IconButton, Tooltip } from "@mui/material";
import { PageHeader } from "@rubi/ui";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { bulletinEditPath } from "@/components/alerts/add-v2/routePaths";
import { ALERT_STATUS } from "@/components/alerts/constants";
import { BulletinResourceResponse } from "@/types";

interface DisplayAlertSingleTopButtonProps {
  currentItem: BulletinResourceResponse;
  mode: ALERT_STATUS;
  searchParams: URLSearchParams;
  setSearchParams: (
    params: URLSearchParams | ((prev: URLSearchParams) => URLSearchParams),
  ) => void;
  readOnly: boolean;
  isCreator: boolean;
  isResolveOpen: boolean;
  setIsResolveOpen: (value: boolean) => void;
  hasUnpublishedResolution: boolean;
  slideoutIdOverride?: number | null;
  isSmall: boolean;
  hasPreviousAlert: () => boolean;
  hasNextAlert: () => boolean;
  onPreviousAlert: () => void;
  onNextAlert: () => void;
  onExit: () => void;
}

export const DisplayAlertSingleTopButton = ({
  currentItem,
  mode,
  searchParams,
  setSearchParams,
  readOnly,
  isCreator,
  isResolveOpen,
  setIsResolveOpen,
  hasUnpublishedResolution,
  slideoutIdOverride,
  isSmall,
  hasPreviousAlert,
  hasNextAlert,
  onPreviousAlert,
  onNextAlert,
  onExit,
}: DisplayAlertSingleTopButtonProps) => {
  const { t } = useTranslation("bulletin");
  const navigate = useNavigate();

  const isDraft = currentItem?.is_draft === 1;
  const alertsOrDrafts = isDraft ? "drafts" : "alerts";
  const idToUse = currentItem?.id;

  const onEditAlert = () => {
    navigate(bulletinEditPath(idToUse ?? ""));
  };

  const ResolveButton = () => {
    if (mode !== ALERT_STATUS.DRAFT && !readOnly) {
      const tooltipTitle = hasUnpublishedResolution
        ? "You have an unpublished comment"
        : "Click to comment";
      const icon = hasUnpublishedResolution ? (
        <ErrorOutline fontSize="small" />
      ) : (
        <CommentOutlined fontSize="small" />
      );
      if (isSmall) {
        return (
          <Tooltip title={tooltipTitle} placement="top">
            <IconButton
              color="primary"
              size="small"
              onClick={() => setIsResolveOpen(!isResolveOpen)}
            >
              {icon}
            </IconButton>
          </Tooltip>
        );
      }
      return (
        <Tooltip title={tooltipTitle} placement="top">
          <Button
            variant="outlined"
            color="primary"
            size="medium"
            startIcon={
              hasUnpublishedResolution ? <ErrorOutline /> : <CommentOutlined />
            }
            onClick={() => setIsResolveOpen(!isResolveOpen)}
            sx={{
              marginRight: "16px",
            }}
          >
            {hasUnpublishedResolution ? "Comment (Pending Changes)" : "Comment"}
          </Button>
        </Tooltip>
      );
    } else return null;
  };

  const ShareButton = () => {
    const accessView = searchParams.get("access");
    const setAccessView = (value: string | null) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        if (value) {
          newParams.set("access", value);
          // Add bulletinId to query params for Access component
          if (idToUse) {
            newParams.set("bulletinId", idToUse.toString());
          }
        } else {
          newParams.delete("access");
          // Clean up bulletinId when leaving access view
          newParams.delete("bulletinId");
        }
        return newParams;
      });
    };

    if (mode !== ALERT_STATUS.DRAFT) {
      if (isSmall) return null;
      const isOpen = accessView === "true";
      const label = isOpen ? "Close Access" : "Manage Access";
      const onClick = () => setAccessView(isOpen ? null : "true");
      return (
        <Button
          variant="outlined"
          color="primary"
          size="medium"
          style={{ marginRight: "16px" }}
          startIcon={<ShareOutlined />}
          onClick={onClick}
        >
          {label}
        </Button>
      );
    } else return null;
  };

  const EditButton = () => {
    if (readOnly || (!readOnly && isCreator)) {
      if (isSmall) {
        return (
          <Tooltip title={t("edit")} placement="top">
            <span>
              <IconButton
                color="primary"
                size="small"
                onClick={onEditAlert}
                disabled={isResolveOpen}
              >
                <EditOutlined fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        );
      }
      return (
        <Button
          variant="outlined"
          color="primary"
          size="medium"
          style={{ marginRight: "16px" }}
          startIcon={<EditOutlined />}
          onClick={onEditAlert}
          disabled={isResolveOpen}
        >
          {t("edit")}
        </Button>
      );
    } else return null;
  };

  const ShowSimilarButton = () => {
    const isShowingSimilar = searchParams.get("similarTo");

    if ((readOnly || mode !== ALERT_STATUS.DRAFT) && !isShowingSimilar) {
      return (
        <Button
          variant="outlined"
          color="primary"
          size="medium"
          style={{
            marginRight: isSmall ? "4px" : "16px",
            paddingRight: isSmall ? 0 : "",
          }}
          onClick={() => {
            setSearchParams((searchParams) => {
              searchParams.set("similarTo", currentItem?.id.toString());
              return searchParams;
            });
          }}
        >
          Find Similar
        </Button>
      );
    } else if (
      (readOnly || mode !== ALERT_STATUS.DRAFT) &&
      isShowingSimilar === currentItem?.id?.toString()
    ) {
      return (
        <Button
          variant="outlined"
          color="primary"
          size="medium"
          style={{
            marginRight: isSmall ? "4px" : "16px",
            paddingRight: isSmall ? 0 : "",
          }}
          startIcon={<CancelOutlined />}
          onClick={() => {
            setSearchParams((searchParams) => {
              searchParams.delete("similarTo");
              return searchParams;
            });
          }}
        >
          Hide Similar
        </Button>
      );
    } else if (
      (readOnly || mode !== ALERT_STATUS.DRAFT) &&
      isShowingSimilar !== currentItem?.id?.toString()
    ) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <Chip
            label={`Similar to ${isShowingSimilar}`}
            color="success"
            variant="outlined"
            title="Go back to reference bulletin"
            onClick={() => {
              setSearchParams((searchParams) => {
                // set bulletinId to similarTo
                if (searchParams.get("similarTo")) {
                  searchParams.set(
                    "bulletinId",
                    searchParams.get("similarTo")!,
                  );
                }
                return searchParams;
              });
            }}
          />
          <Button
            variant="outlined"
            color="primary"
            size="medium"
            onClick={() => {
              setSearchParams((searchParams) => {
                searchParams.set("similarTo", currentItem?.id.toString());
                return searchParams;
              });
            }}
          >
            Find Similar
          </Button>
        </div>
      );
    } else return null;
  };

  const PreviousButton = () => {
    return (
      <Button
        variant="outlined"
        color="primary"
        className="border-radius-rounded p-0"
        size="medium"
        onClick={onPreviousAlert}
        style={{
          minWidth: "48px",
          minHeight: "48px",
        }}
        disabled={isResolveOpen || !hasPreviousAlert()}
      >
        <ArrowBack />
      </Button>
    );
  };

  const NextButton = () => {
    return (
      <Button
        variant="outlined"
        color="primary"
        className="border-radius-rounded"
        onClick={onNextAlert}
        disabled={isResolveOpen || !hasNextAlert()}
        style={{
          minWidth: "48px",
          minHeight: "48px",
          padding: 0,
        }}
      >
        <ArrowForward />
      </Button>
    );
  };

  const topButtons = () => {
    const resolveButton = ResolveButton();
    const shareButton = ShareButton();
    const editButton = EditButton();
    const showSimilarButton = ShowSimilarButton();
    const showExit =
      mode === ALERT_STATUS.DRAFT || slideoutIdOverride === undefined;

    return (
      <PageHeader
        onBack={showExit ? onExit : undefined}
        backArrowAriaLabel={t("exit_bulletin")}
        right={
          <>
            {resolveButton}
            {shareButton}
            {editButton}
            {slideoutIdOverride && (
              <>
                {showSimilarButton}
                <IconButton
                  aria-label="open in new tab"
                  color="primary"
                  onClick={() => {
                    window.open(
                      `/rubialert/${alertsOrDrafts}/${currentItem?.id}/single`,
                      "_blank",
                    );
                  }}
                >
                  <OpenInNewOutlinedIcon />
                </IconButton>
              </>
            )}
          </>
        }
      />
    );
  };

  const mobileBottomButtons = () => {
    const showBottomButtons = hasPreviousAlert() || hasNextAlert();
    if (!showBottomButtons) return null;
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          position: "fixed",
          bottom: "0",
          left: "0",
          padding: "16px",
          bgcolor: "background.paper",
          boxShadow: "0px -4px 4px rgba(0, 0, 0, 0.25)",
        }}
      >
        <PreviousButton />
        <NextButton />
      </Box>
    );
  };

  const desktopBottomButtons = () => {
    const showBottomButtons = hasPreviousAlert() || hasNextAlert();
    if (!showBottomButtons) return null;
    return (
      <div className="desktop-bottom-buttons d-flex mb-3">
        <div className="mr-3">
          <PreviousButton />
        </div>
        <div>
          <NextButton />
        </div>
      </div>
    );
  };

  return {
    topButtons,
    mobileBottomButtons,
    desktopBottomButtons,
  };
};
